import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import SiteFooter from "../components/SiteFooter";

export default function Receipt() {
  const { id } = useParams();
  const location = useLocation();
  const verifyError = location.state?.verifyError || null;

  const [registration, setRegistration] = useState(null);
  const [payment, setPayment] = useState(null);
  const [receiptMeta, setReceiptMeta] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    async function load() {
      if (!id) return;
      try {
        setLoading(true);
        setErr("");
        const { data } = await api.get(`/api/registrations/${id}/receipt`);
        if (!alive) return;

        setRegistration(data.registration || null);
        setPayment(data.payment || null);
        setReceiptMeta(data.receipt_meta || null);
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.message || "Failed to load receipt");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [id]);

  const formatDateTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const receiptNo = registration?.receipt_no || (registration?.id ? `REG-${registration.id}` : "");
  const receiptDate = registration?.created_at ? formatDateTime(registration.created_at) : "-";

  const amountPaid =
    payment?.amount_minor != null
      ? `₹ ${(payment.amount_minor / 100).toFixed(2)}`
      : registration?.final_amount != null
      ? `₹ ${Number(registration.final_amount).toFixed(2)}`
      : "-";

  const titleText = (receiptMeta?.title && String(receiptMeta.title).trim()) || "Receipt";

  const signatureUrl = receiptMeta?.signature_url || null;
  const signatureLabel =
    (receiptMeta?.signature_label && String(receiptMeta.signature_label).trim()) ||
    "Authorized Signature";

  const paymentStatusPill = () => {
    const s = String(payment?.status || "").toUpperCase();

    let label = "Payment Pending";
    let bg = "#fef3c7";
    let color = "#92400e";

    if (s === "SUCCESS" || s === "COMPLETED") {
      label = "Payment Successful";
      bg = "#bbf7d0";
      color = "#166534";
    } else if (s === "FAILED") {
      label = "Payment Failed";
      bg = "#fecaca";
      color = "#b91c1c";
    }

    return (
      <span
        style={{
          padding: "6px 12px",
          borderRadius: 999,
          background: bg,
          color,
          fontSize: 12,
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
    );
  };

  const handlePrint = () => window.print();

  // Download PDF via print (Save as PDF) — hide ONLY buttons (never hide signature)
  const handleDownloadPdf = () => {
    const originalTitle = document.title;
    document.body.classList.add("receipt-downloading");
    document.title = receiptNo ? `${receiptNo}-Receipt` : "Receipt";
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
      document.body.classList.remove("receipt-downloading");
    }, 900);
  };

  return (
    <>
      <style>{`
        @page { margin: 10mm; }

        /* Download PDF: hide only buttons/back (signature is inside payment box, so unaffected) */
        .receipt-downloading .receipt-actions-buttons { display: none !important; }

        @media print {
          body { background: #fff !important; }
          .receipt-page-wrap { padding: 0 !important; background: #fff !important; }
          .receipt-card { box-shadow: none !important; border: 1px solid #e5e7eb !important; }
          .receipt-actions-buttons { display: none !important; }
          .print-hide-footer { display: none !important; }
          .receipt-card { page-break-inside: avoid; }
        }
      `}</style>

      <div
        className="receipt-page-wrap"
        style={{
          minHeight: "100vh",
          background: "#f3f4f6",
          padding: "22px 10px 28px",
        }}
      >
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          {verifyError && (
            <div
              style={{
                marginBottom: 10,
                padding: "8px 10px",
                borderRadius: 10,
                background: "#fef2f2",
                color: "#b91c1c",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {verifyError}
            </div>
          )}

          {err && (
            <div
              style={{
                marginBottom: 10,
                padding: "8px 10px",
                borderRadius: 10,
                background: "#fef2f2",
                color: "#b91c1c",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {err}
            </div>
          )}

          {loading ? (
            <div
              className="receipt-card"
              style={{
                background: "#fff",
                borderRadius: 18,
                padding: 18,
                boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
              }}
            >
              Loading…
            </div>
          ) : !registration ? (
            <div
              className="receipt-card"
              style={{
                background: "#fff",
                borderRadius: 18,
                padding: 18,
                boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
              }}
            >
              Receipt not found.
            </div>
          ) : (
            <div
              className="receipt-card"
              style={{
                background: "#fff",
                borderRadius: 20,
                padding: "18px 20px 16px",
                boxShadow: "0 14px 34px rgba(15,23,42,0.12)",
                border: "1px solid #eef2f7",
                position: "relative",
              }}
            >
              {/* small Receipt word */}
              <div
                style={{
                  position: "absolute",
                  left: 14,
                  bottom: 10,
                  fontSize: 10,
                  letterSpacing: "0.22em",
                  color: "#94a3b8",
                  textTransform: "uppercase",
                }}
              >
                Receipt
              </div>

              {/* Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 10,
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 650,
                    lineHeight: 1.15,
                    color: "#0f172a",
                    maxWidth: "78%",
                  }}
                >
                  {titleText}
                </div>
                {paymentStatusPill()}
              </div>

              {/* Receipt No + Date */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                  fontSize: 13,
                  marginTop: 8,
                  marginBottom: 10,
                }}
              >
                <div>
                  <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase" }}>
                    Receipt No
                  </div>
                  <div style={{ fontWeight: 600, color: "#0f172a" }}>{receiptNo}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase" }}>
                    Registered Date
                  </div>
                  <div style={{ fontWeight: 600, color: "#0f172a" }}>{receiptDate}</div>
                </div>
              </div>

              <hr style={{ border: "none", borderTop: "1px solid #e5e7eb", margin: "10px 0 12px" }} />

              {/* Details */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 24,
                  fontSize: 13,
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <Field label="Name" value={registration.full_name} />
                  <Field label="Mobile" value={registration.mobile} />
                  <Field
                    label="Gender"
                    value={
                      registration.gender === "M"
                        ? "Male"
                        : registration.gender === "F"
                        ? "Female"
                        : "-"
                    }
                  />
                  <Field label="Address" value={registration.address} />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <Field label="Course" value={registration.course_name || "-"} />
                  <Field label="Amount Paid (₹)" value={amountPaid} />
                  <Field label="College" value={registration.college_name || "-"} />
                  <Field label="Email" value={registration.email || "-"} />
                </div>
              </div>

              {/* ✅ Payment details with signature bottom-right INSIDE */}
              <div
                style={{
                  marginTop: 16,
                  padding: 14,
                  borderRadius: 16,
                  border: "1px dashed #cbd5e1",
                  background: "#f9fafb",
                  fontSize: 13,
                  position: "relative",
                  // reserve space for signature on the right so text never overlaps
                  paddingRight: signatureUrl ? 230 : 14,
                  minHeight: signatureUrl ? 110 : "auto",
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 6, color: "#0f172a" }}>
                  Payment Details
                </div>

                <div style={{ marginTop: 4 }}>
                  <span style={{ fontWeight: 600 }}>Order Id: </span>
                  <span style={{ wordBreak: "break-all", fontWeight: 400 }}>
                    {payment?.razorpay_order_id || registration.razorpay_order_id || "-"}
                  </span>
                </div>

                <div style={{ marginTop: 2 }}>
                  <span style={{ fontWeight: 600 }}>Payment Id: </span>
                  <span style={{ wordBreak: "break-all", fontWeight: 400 }}>
                    {payment?.razorpay_payment_id || "-"}
                  </span>
                </div>

                {signatureUrl ? (
                  <div
                    style={{
                      position: "absolute",
                      right: 12,
                      bottom: 10,
                      width: 200,
                      textAlign: "center",
                    }}
                  >
                    <img
                      src={signatureUrl}
                      alt="Digital Signature"
                      style={{
                        maxWidth: 180,
                        maxHeight: 55,
                        width: "auto",
                        height: "auto",
                        display: "block",
                        margin: "0 auto",
                        objectFit: "contain",
                      }}
                    />
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                      {signatureLabel}
                    </div>
                  </div>
                ) : null}
              </div>

              <div style={{ textAlign: "center", marginTop: 14, fontSize: 13, color: "#475569" }}>
                Thank you for registering with YogaPay.
              </div>

              {/* Buttons (hide on print/download) */}
              <div
                className="receipt-actions-buttons"
                style={{
                  marginTop: 14,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <Link
                  to="/"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 14px",
                    borderRadius: 999,
                    border: "1px solid #e5e7eb",
                    color: "#0f172a",
                    fontSize: 13,
                    textDecoration: "none",
                    background: "#f9fafb",
                    fontWeight: 600,
                  }}
                >
                  <span style={{ fontSize: 18 }}>←</span>
                  <span>Back to Home</span>
                </Link>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={handleDownloadPdf}
                    style={{
                      padding: "11px 16px",
                      borderRadius: 999,
                      border: "1px solid #e2e8f0",
                      background: "#ffffff",
                      color: "#0f172a",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Download PDF
                  </button>

                  <button
                    type="button"
                    onClick={handlePrint}
                    style={{
                      padding: "11px 18px",
                      borderRadius: 999,
                      border: "none",
                      background: "#047857",
                      color: "#ffffff",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Print Receipt
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer should not print */}
      <div className="print-hide-footer">
        <SiteFooter />
      </div>
    </>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>{label}</div>
      <div style={{ fontWeight: 500, color: "#0f172a" }}>{value || "-"}</div>
    </div>
  );
}
